(function registerEfLotteryCard() {
  const DEFAULT_API_DOMAIN = "https://api-a.ecoflow.com";
  const DEFAULT_API_ORIGIN = "https://us.ecoflow.com";
  const DEFAULT_API_REFERER = "https://us.ecoflow.com/";
  const DETAIL_API_PATH = "/integral/web/rewardDraw/getRewardDrawById";
  const DRAW_API_PATH = "/integral/web/rewardDraw/subscribeDraw";
  const SUBSCRIPTION_API_PATH = "/website/subscribe/new";
  const ENABLE_MOCK_FALLBACK = true;
  const MISSING_LOTTERY_CONFIG_MESSAGE = "Missing lottery configuration.";
  const DRAW_ERROR_MESSAGES = {
    "2433": "This email has reached the participation limit.",
    "1001": "This IP has reached the participation limit. ",
  };
  const DEFAULT_DRAW_ERROR_MESSAGE = "An error occurred. Please try again.";
  const OPENING_DELAY_MS = 120;
  const SHAKE_DURATION_MS = 760;

  function getSettings(settings) {
    return settings || window.efLotteryCardSettingJsons || {};
  }

  function getPrizes(prizes) {
    if (Array.isArray(prizes)) {
      return prizes;
    }

    return Array.isArray(window.efLotteryCardPrizes)
      ? window.efLotteryCardPrizes
      : [];
  }

  function trimValue(value) {
    return value == null ? "" : String(value).trim();
  }

  function resolveDrawVersion(rewardDrawInfo) {
    const version = rewardDrawInfo?.version;
    if (version == null) {
      return "";
    }

    if (typeof version === "number" && Number.isFinite(version)) {
      return version;
    }

    return trimValue(version);
  }

  function isMissingDrawVersion(version) {
    return version === "" || version == null;
  }

  function shouldUseMockFallback(error) {
    if (!ENABLE_MOCK_FALLBACK) {
      return false;
    }

    if (error?.isApiResponseError) {
      return false;
    }

    return trimValue(error?.message) !== MISSING_LOTTERY_CONFIG_MESSAGE;
  }

  function joinUrl(domain, path) {
    const safeDomain = trimValue(domain).replace(/\/+$/, "");
    const safePath = trimValue(path);
    if (!safeDomain || !safePath) {
      return "";
    }

    return `${safeDomain}${safePath.charAt(0) === "/" ? "" : "/"}${safePath}`;
  }

  function isSuccessfulResponse(responseData) {
    const code = responseData?.code;
    if (code != null && trimValue(code) !== "") {
      return trimValue(code) === "0";
    }

    return responseData?.message === "Success" || responseData?.data != null;
  }

  function getResponsePayload(responseData) {
    return responseData?.data || responseData || {};
  }

  function resolveDrawErrorMessage(responseData) {
    const code = trimValue(responseData?.code);
    return DRAW_ERROR_MESSAGES[code] || DEFAULT_DRAW_ERROR_MESSAGE;
  }

  function getLookupValue(payload, lookupKeys) {
    for (let i = 0; i < lookupKeys.length; i += 1) {
      const value = payload[lookupKeys[i]];
      if (value != null && trimValue(value) !== "") {
        return value;
      }
    }

    return "";
  }

  function resolvePrizeLookupValue(payload) {
    const field = payload?.field;
    if (field?.value != null) {
      if (Array.isArray(field.value) && field.value.length > 0) {
        return String(field.value[0]);
      }

      if (trimValue(field.value) !== "") {
        return String(field.value);
      }
    }

    return getLookupValue(payload, [
      "prizeIndex",
      "prizeValue",
      "rewardId",
    ]);
  }

  function normalizeDrawField(field) {
    if (!field || trimValue(field.id) === "") {
      return null;
    }

    const type = trimValue(field.type) || "multiple";
    const normalized = {
      id: trimValue(field.id),
      type,
      overwrite: field.overwrite === true || field.overwrite === "true",
    };

    if (type === "multiple") {
      normalized.value = Array.isArray(field.value)
        ? field.value.map((item) => Number(item))
        : String(field.value)
            .split(",")
            .map((item) => Number(trimValue(item)))
            .filter((item) => !Number.isNaN(item));
    } else if (type === "single") {
      const rawValue = Array.isArray(field.value) ? field.value[0] : field.value;
      normalized.value = Number(rawValue);
    } else {
      normalized.value = field.value;
    }

    return normalized;
  }

  function mergeSubscriptionFields(baseFields, drawField) {
    const normalizedField = normalizeDrawField(drawField);
    if (!normalizedField) {
      return baseFields;
    }

    const nextFields = [...baseFields];
    const existingIndex = nextFields.findIndex(
      (field) => String(field.id) === String(normalizedField.id)
    );

    if (existingIndex >= 0) {
      nextFields[existingIndex] = normalizedField;
      return nextFields;
    }

    nextFields.push(normalizedField);
    return nextFields;
  }

  function buildSubscriptionFields(settings, drawField) {
    if (typeof createFieldsArray !== "function") {
      return mergeSubscriptionFields([], drawField);
    }

    return mergeSubscriptionFields(createFieldsArray(settings), drawField);
  }

  function getEmailSubscriptionConfig() {
    const themeConfig =
      (typeof window !== "undefined" && window.theme?.emailSubscription) ||
      (typeof theme !== "undefined" ? theme.emailSubscription : null);

    return themeConfig || null;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  async function waitForSubscriptionHelpers(maxWaitMs = 5000) {
    const startedAt = Date.now();

    while (Date.now() - startedAt < maxWaitMs) {
      if (typeof getContactListId === "function") {
        return true;
      }

      await wait(50);
    }

    return typeof getContactListId === "function";
  }

  function getConfiguredPrizeById(prizes, prizeId) {
    return getPrizes(prizes).find((prize) => prize.id === prizeId) || null;
  }

  // Resolve a prize field by matching the backend lookup value against
  // configured `api_value`, falling back to a 1-based index lookup.
  function resolvePrizeFieldByLookup(prizes, lookupValue, getField) {
    if (!lookupValue) {
      return "";
    }

    const match = prizes.find(
      (prize) => String(prize.api_value) === String(lookupValue)
    );
    if (match && getField(match)) {
      return getField(match);
    }

    const index = Number(lookupValue) - 1;
    if (Number.isInteger(index) && index >= 0 && prizes[index]) {
      return getField(prizes[index]) || "";
    }

    return "";
  }

  function normalizeAward(award) {
    return {
      id: trimValue(award?.id),
      drawId: trimValue(award?.drawId),
      iconUrl: trimValue(award?.iconUrl),
      isDisplayIcon: award?.isDisplayIcon !== false,
      isDisplayName: award?.isDisplayName !== false,
      points: award?.points || 0,
      prizeId: trimValue(award?.prizeId),
      prizeName: trimValue(award?.prizeName),
      tips: trimValue(award?.tips),
      type: trimValue(award?.type),
    };
  }

  function normalizeAwards(awards) {
    return Array.isArray(awards) ? awards.map(normalizeAward) : [];
  }

  function buildDetailRequest(settings) {
    const apiUrl = joinUrl(DEFAULT_API_DOMAIN, DETAIL_API_PATH);

    return {
      apiUrl,
      body: {
        id: trimValue(settings.draw_id),
      },
    };
  }

  function buildDrawRequest(settings, rewardDrawInfo, email) {
    const apiUrl = joinUrl(DEFAULT_API_DOMAIN, DRAW_API_PATH);
    const drawId = trimValue(settings.draw_id || rewardDrawInfo?.id);
    const version = resolveDrawVersion(rewardDrawInfo);

    return {
      apiUrl,
      body: {
        drawId,
        email,
        version,
      },
    };
  }

  function buildSubscriptionRequest() {
    return {
      apiUrl: joinUrl(DEFAULT_API_DOMAIN, SUBSCRIPTION_API_PATH),
    };
  }

  async function postJson({ apiUrl, body, label }) {
    if (!apiUrl) {
      throw new Error(`${label} api is missing.`);
    }

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-appid": "1",
        Origin: DEFAULT_API_ORIGIN,
        Referer: DEFAULT_API_REFERER,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`${label} request failed with status ${response.status}`);
    }

    const data = await response.json();

    if (!isSuccessfulResponse(data)) {
      const error = new Error(
        data?.message || `${label} request was not successful.`
      );
      error.isApiResponseError = true;
      error.responseData = data;
      throw error;
    }

    return data;
  }

  function selectMockPrize(prizes) {
    const prizeList = getPrizes(prizes);
    return (
      prizeList.find((prize) => trimValue(prize.api_value) !== "") ||
      prizeList[0] ||
      null
    );
  }

  function resolvePrizeName(responseData, prizes) {
    const prizeList = getPrizes(prizes);
    const payload = getResponsePayload(responseData);
    const directKeys = [
      "name",
      "prizeName",
      "prizeTitle",
      "rewardName",
      "rewardTitle",
      "prize",
      "reward",
    ];

    const directName = getLookupValue(payload, directKeys);
    if (directName) {
      return trimValue(directName);
    }

    const lookupValue = resolvePrizeLookupValue(payload);

    return resolvePrizeFieldByLookup(prizeList, lookupValue, (prize) => prize.title);
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimValue(email));
  }

  const component = (options = {}) => ({
    settings: getSettings(options.settings),
    prizes: getPrizes(options.prizes),
    email: "",
    errMsg: "",
    loading: false,
    isSuccess: false,
    isGiftShaking: false,
    isGiftOpening: false,
    showPrizeReveal: false,
    wonPrizeId: "",
    prizeName: "",
    rewardDrawInfo: null,
    rewardDrawVersion: "",
    rewardDrawAwards: [],
    buttonLabel: "",
    loadingLabel: "",
    detailLoading: false,
    rewardDrawInfoPromise: null,
    revealTimer: null,
    wonDrawPayload: null,

    init() {
      const settings = this.settings;
      this.buttonLabel = settings.button_label || "Enter To Win";
      this.loadingLabel = settings.loading_text || "Loading...";
      this.rewardDrawInfoPromise = this.loadRewardDrawInfo();
    },

    // Clear pending reveal timers so callbacks never fire on a destroyed
    // component (e.g. when the section is removed in the Theme Editor).
    destroy() {
      if (this.revealTimer) {
        clearTimeout(this.revealTimer);
        this.revealTimer = null;
      }
    },

    get displayButtonLabel() {
      return this.loading ? this.loadingLabel : this.buttonLabel;
    },

    resetLotteryState() {
      this.errMsg = "";
      this.prizeName = "";
      this.wonPrizeId = "";
      this.wonDrawPayload = null;
      this.showPrizeReveal = false;
      this.isGiftShaking = false;
      this.isGiftOpening = false;
    },

    focusEmailInput() {
      this.$nextTick(() => {
        this.$refs.emailInput?.focus();
      });
    },

    resolveWonPrizeId(responseData) {
      const prizes = this.prizes;
      const payload = getResponsePayload(responseData);
      const lookupValue = resolvePrizeLookupValue(payload);

      const lookupId = resolvePrizeFieldByLookup(
        prizes,
        lookupValue,
        (prize) => prize.id
      );
      if (lookupId) {
        return lookupId;
      }

      const prizeName = resolvePrizeName(responseData, prizes);
      if (!prizeName) {
        return "";
      }

      const titleMatch = prizes.find((prize) => prize.title === prizeName);
      return titleMatch?.id || "";
    },

    resolvePrizeFromResponse(responseData) {
      const prizes = this.prizes;
      const wonPrizeId = this.resolveWonPrizeId(responseData);
      return prizes.find((prize) => prize.id === wonPrizeId) || null;
    },

    getAwardForPrize(prizeId) {
      const configuredPrize = getConfiguredPrizeById(this.prizes, prizeId);
      if (!configuredPrize) {
        return null;
      }

      const apiValue = trimValue(configuredPrize.api_value);
      return (
        this.rewardDrawAwards.find(
          (award) => apiValue && award.prizeId === apiValue
        ) || null
      );
    },

    getPrizeTitle(prizeId, fallbackTitle) {
      const award = this.getAwardForPrize(prizeId);
      if (award?.isDisplayName && award.prizeName) {
        return award.prizeName;
      }

      return fallbackTitle || "";
    },

    getPrizeIconUrl(prizeId) {
      if (this.isSuccess && this.wonPrizeId === prizeId && this.wonDrawPayload) {
        const payload = this.wonDrawPayload;
        if (payload.isDisplayIcon !== false && trimValue(payload.iconUrl)) {
          return trimValue(payload.iconUrl);
        }
      }

      const award = this.getAwardForPrize(prizeId);
      if (award?.isDisplayIcon && award.iconUrl) {
        return award.iconUrl;
      }

      return "";
    },

    getPrizeTips(prizeId) {
      return this.getAwardForPrize(prizeId)?.tips || "";
    },

    get activityHeading() {
      return this.settings.heading || "";
    },

    get activityDescriptionHtml() {
      return (
        this.settings.description ||
        trimValue(this.rewardDrawInfo?.description) ||
        trimValue(this.rewardDrawInfo?.notes) ||
        ""
      );
    },

    async requestRewardDrawInfo(settings) {
      const request = buildDetailRequest(settings);
      return postJson({
        apiUrl: request.apiUrl,
        body: request.body,
        label: "rewardDrawDetail",
      });
    },

    async loadRewardDrawInfo() {
      const settings = this.settings;
      const drawId = trimValue(settings.draw_id);
      if (!drawId) {
        this.rewardDrawInfo = null;
        this.rewardDrawVersion = "";
        this.rewardDrawAwards = [];
        this.detailLoading = false;
        return;
      }

      this.detailLoading = true;

      try {
        const data = await this.requestRewardDrawInfo(settings);
        const payload = getResponsePayload(data);
        this.rewardDrawInfo = Array.isArray(payload)
          ? payload[0] || null
          : payload && typeof payload === "object"
            ? payload
            : null;
        this.rewardDrawVersion = resolveDrawVersion(this.rewardDrawInfo);
        this.rewardDrawAwards = normalizeAwards(this.rewardDrawInfo?.drawAwards);
      } catch (error) {
        this.rewardDrawInfo = null;
        this.rewardDrawVersion = "";
        this.rewardDrawAwards = [];
        console.warn("[ef-lottery-card] Reward draw detail request failed.", error);
      } finally {
        this.detailLoading = false;
      }
    },

    async waitForRewardDrawInfo() {
      if (!this.rewardDrawInfoPromise) {
        return;
      }

      try {
        await this.rewardDrawInfoPromise;
      } catch (error) {
        console.warn("[ef-lottery-card] Reward draw detail request is unavailable.", error);
      }
    },

    async requestPrize(settings) {
      const request = buildDrawRequest(settings, this.rewardDrawInfo, this.email);

      if (
        !request.body.drawId ||
        isMissingDrawVersion(request.body.version) ||
        !request.apiUrl
      ) {
        throw new Error(MISSING_LOTTERY_CONFIG_MESSAGE);
      }

      const responseData = await postJson({
        apiUrl: request.apiUrl,
        body: request.body,
        label: "subscribeDraw",
      });

      return responseData;
    },

    async requestEmailSubscription(settings, email, drawField) {
      const helpersReady = await waitForSubscriptionHelpers();
      if (!helpersReady) {
        throw new Error(
          "subscription-form-common.js is not loaded."
        );
      }

      const contactListId = getContactListId(settings);
      const subscriptionConfig = getEmailSubscriptionConfig();

      if (!trimValue(contactListId)) {
        throw new Error(
          "Email subscription contact_list_id is missing in section settings."
        );
      }

      const subscriptionRequest = buildSubscriptionRequest();
      if (!subscriptionRequest.apiUrl) {
        throw new Error("Email subscription api is missing.");
      }

      if (!trimValue(subscriptionConfig?.apiKey) || !trimValue(subscriptionConfig?.xAppid)) {
        throw new Error(
          "Email subscription apiKey or xAppid is missing in theme settings."
        );
      }

      const fields = buildSubscriptionFields(settings, drawField);
      const subscribeInfoCookie = window.getCookieForEmarsys?.("subscribe_info");
      let eventTracking = null;

      if (subscribeInfoCookie) {
        try {
          const decodedCookie = decodeURIComponent(subscribeInfoCookie);
          eventTracking = JSON.parse(decodedCookie);
          delete eventTracking.ts;
        } catch (error) {
          console.warn("[ef-lottery-card] Failed to parse subscribe_info cookie.", error);
        }
      }

      const apiBody = {
        email,
        contactListId,
        fields,
        ...(eventTracking && { eventTracking }),
      };

      const traceId =
        window.traceId ||
        (typeof createTraceId === "function" ? createTraceId() : "");

      const response = await fetch(subscriptionRequest.apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apiKey: subscriptionConfig.apiKey,
          traceId,
          "X-appid": subscriptionConfig.xAppid,
          "Accepted-Language": subscriptionConfig.acceptedLanguage,
        },
        body: JSON.stringify(apiBody),
      });

      const data = await response.json();

      if (!response.ok || data.message !== "Success") {
        throw new Error(data?.message || "Email subscription request failed.");
      }

      return data;
    },

    revealPrize(prize, drawPayload) {
      if (!prize) {
        return;
      }

      const payload = drawPayload || {};
      const displayName =
        payload.isDisplayName !== false && trimValue(payload.name)
          ? trimValue(payload.name)
          : prize.title || "";

      this.prizeName = displayName;
      this.wonPrizeId = prize.id || "";
      this.wonDrawPayload = payload;
      this.isSuccess = true;
      this.showPrizeReveal = false;
      this.isGiftShaking = true;
      this.isGiftOpening = false;
      this.email = "";

      if (this.revealTimer) {
        clearTimeout(this.revealTimer);
      }

      this.revealTimer = setTimeout(() => {
        this.isGiftShaking = false;
        this.isGiftOpening = true;

        this.revealTimer = setTimeout(() => {
          this.showPrizeReveal = true;
          this.revealTimer = null;
        }, OPENING_DELAY_MS);
      }, SHAKE_DURATION_MS);
    },

    validateEmailInput(settings) {
      if (this.email === "") {
        return settings.email_empty_error || "Please enter your email address.";
      }

      if (!isValidEmail(this.email)) {
        return (
          settings.email_invalid_error ||
          "Please enter a valid email address (Example: name@domain.com)"
        );
      }

      return "";
    },

    async resolvePrize(settings) {
      let drawResponse = null;

      try {
        drawResponse = await this.requestPrize(settings);
        const drawPayload = getResponsePayload(drawResponse);
        const prize = this.resolvePrizeFromResponse(drawResponse);

        if (!prize) {
          throw new Error("Prize response did not match configuration.");
        }

        return { prize, drawPayload, drawResponse };
      } catch (error) {
        if (!shouldUseMockFallback(error)) {
          throw error;
        }

        const drawPayload = drawResponse
          ? getResponsePayload(drawResponse)
          : null;

        return {
          prize: selectMockPrize(this.prizes),
          drawPayload,
          drawResponse,
        };
      }
    },

    resolveErrorMessage(error, settings) {
      if (error?.isApiResponseError) {
        return resolveDrawErrorMessage(error.responseData);
      }

      return (
        settings.api_error_message ||
        "Oops... Something went wrong, please refresh and try again."
      );
    },

    startGiftAnimation() {
      if (this.loading) {
        return;
      }

      this.resetLotteryState();
      const settings = this.settings;

      this.errMsg = this.validateEmailInput(settings);
      if (this.errMsg) {
        this.focusEmailInput();
        return;
      }

      const prize = selectMockPrize(this.prizes);
      if (!prize) {
        this.errMsg = this.resolveErrorMessage(
          new Error("No prize is configured."),
          settings
        );
        return;
      }

      this.revealPrize(prize);
    },

    async startLottery() {
      if (this.loading) {
        return;
      }

      this.resetLotteryState();
      const settings = this.settings;

      this.errMsg = this.validateEmailInput(settings);
      if (this.errMsg) {
        this.focusEmailInput();
        return;
      }

      this.loading = true;
      const email = this.email;
      try {
        await this.waitForRewardDrawInfo();

        const { prize, drawPayload, drawResponse } = await this.resolvePrize(
          settings
        );
        if (!prize) {
          throw new Error("No prize is configured.");
        }

        if (drawResponse) {
          try {
            await this.requestEmailSubscription(
              settings,
              email,
              drawPayload?.field
            );
          } catch (subscriptionError) {
            console.error(
              "[ef-lottery-card] Email subscription failed after draw.",
              subscriptionError
            );
          }
        } else {
          console.warn(
            "[ef-lottery-card] Skipped email subscription because draw API did not succeed."
          );
        }

        this.revealPrize(prize, drawPayload);
      } catch (error) {
        this.errMsg = this.resolveErrorMessage(error, settings);
        console.warn(error);
      } finally {
        this.loading = false;
      }
    },
  });

  function registerEfLotteryCardData() {
    Alpine.data("efLotteryCard", component);
  }

  function initEfLotteryCardTrees() {
    if (typeof document?.querySelectorAll !== "function") {
      return;
    }

    document
      .querySelectorAll(".ef-lottery-card[x-data*='efLotteryCard']")
      .forEach((root) => {
        if (!root._x_dataStack?.length) {
          Alpine.initTree(root);
        }
      });
  }

  if (window.Alpine) {
    registerEfLotteryCardData();
    initEfLotteryCardTrees();
    return;
  }

  document.addEventListener("alpine:init", () => {
    registerEfLotteryCardData();
  });
})();
